import classNames from 'classnames'
import React from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import 'browser/components/atomic-elements/atoms/section/_section.scss'
import { SectionHeader } from 'browser/components/atomic-elements/atoms/section/section-header/section-header'
import { REVEAL_EVENT } from 'browser/components/atomic-elements/atoms/form-field/form-field-shell/reveal-form-field'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { translateString } from 'shared-libs/helpers/utils'

/**
 * @uiComponent
 */
interface ISectionProps extends IBaseProps {
  id?: string
  frames?: FramesManager
  bodyClassName?: string
  footerClassName?: string
  footerControls?: React.ReactElement<any>
  isCollapsable?: boolean
  isCollapsed?: boolean
  headerClassName?: string
  headerStyle?: React.CSSProperties
  style?: React.CSSProperties
  bodyStyle?: React.CSSProperties
  headerControls?: React.ReactElement<any>
  hideEmptyFields?: boolean
  hideHeaderBorder?: boolean
  optionsPopoverList?: React.ReactElement<any>
  optionsPopoverTetherOptions?: object
  reduceMargin: boolean
  showHideEmptyFieldsControls?: boolean
  subtitle?: string
  title?: string
  titleElement?: React.ReactElement<any>
  hideWhenAllFieldsEmpty?: boolean
}

interface ISectionStates {
  isCollapsed: boolean
  hideEmptyFields: boolean
  allFieldsEmpty: boolean
}

export class Section extends React.Component<ISectionProps, ISectionStates> {
  public static defaultProps: ISectionProps = {
    isCollapsed: false,
    reduceMargin: false,
    showHideEmptyFieldsControls: false,
  }

  private sectionBodyRef = React.createRef<HTMLDivElement>()

  constructor(props) {
    super(props)
    this.state = {
      hideEmptyFields: this.props.hideEmptyFields,
      allFieldsEmpty: false,
      isCollapsed: this.props.isCollapsed,
    }
    this.handleIsCollapsedChange = this.handleIsCollapsedChange.bind(this)
    this.handleHideEmptyFieldsChange = this.handleHideEmptyFieldsChange.bind(this)
  }

  public UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isCollapsed != this.props.isCollapsed) {
      this.setState({
        isCollapsed: nextProps.isCollapsed
      })
    }
  }

  public componentDidMount() {
    if (this.props.hideWhenAllFieldsEmpty) {
      this.updateHiddenFieldState()
    }
    // Uncollapse when a descendant field asks to be revealed (e.g. save-time
    // validation focusing the first error).
    this.sectionBodyRef.current?.addEventListener(REVEAL_EVENT, this.handleReveal)
  }

  public componentWillUnmount() {
    this.sectionBodyRef.current?.removeEventListener(REVEAL_EVENT, this.handleReveal)
  }

  private handleReveal = () => {
    if (this.state.isCollapsed) {
      this.setState({ isCollapsed: false })
    }
  }

  public componentDidUpdate() {
    if (this.props.hideWhenAllFieldsEmpty) {
      this.updateHiddenFieldState()
    }
  }

  public render() {
    const {
      id,
      frames,
      className,
      headerClassName,
      headerStyle,
      style,
      bodyStyle,
      hideHeaderBorder,
      isCollapsable,
      showHideEmptyFieldsControls,
      title,
      subtitle,
      headerControls,
      optionsPopoverList,
      optionsPopoverTetherOptions,
      bodyClassName,
      titleElement,
      children,
      reduceMargin,
      hideWhenAllFieldsEmpty
    } = this.props
    const {
      isCollapsed,
      hideEmptyFields,
      allFieldsEmpty,
    } = this.state
    const margin = reduceMargin ? 'mb2' : 'mb4'

    const translationTable = frames?.getContext('translationTable')
    const translatedLabel = translateString(title, translationTable)

    return (
      <div id={id} className={classNames('c-section mb2', margin, className, {
        'is-allFieldsEmpty': allFieldsEmpty && hideWhenAllFieldsEmpty,
      })} style={style}>
        <SectionHeader
          className={headerClassName}
          isCollapsable={isCollapsable}
          hideHeaderBorder={hideHeaderBorder}
          isCollapsed={isCollapsed}
          onIsCollapsedChange={this.handleIsCollapsedChange}
          onHideEmptyFieldsChange={this.handleHideEmptyFieldsChange}
          showHideEmptyFieldsControls={showHideEmptyFieldsControls}
          hideEmptyFields={hideEmptyFields}
          title={translatedLabel}
          titleElement={titleElement}
          subtitle={subtitle}
          headerControls={headerControls}
          optionsPopoverList={optionsPopoverList}
          optionsPopoverTetherOptions={optionsPopoverTetherOptions}
          style={headerStyle}
        />
        <div
          ref={this.sectionBodyRef}
          className={classNames('c-sectionBody', bodyClassName, {
            'is-collapsed': isCollapsed,
            'is-hideEmptyFields': hideEmptyFields,
          })}
          style={bodyStyle}
        >
          {children}
        </div>
        {this.renderSectionFooter()}
      </div>
    )
  }

  private handleIsCollapsedChange(value) {
    this.setState({isCollapsed: value})
  }

  private handleHideEmptyFieldsChange(value) {
    this.setState({hideEmptyFields: value})
  }

  private renderSectionFooter() {
    const {
      footerControls,
      footerClassName,
    } = this.props

    if (footerControls) {
      return (
        <div className={classNames('c-sectionFooter', footerClassName)}>
          {footerControls}
        </div>
      )
    }
  }

  // Check if all direct child form groups are hidden (via hasNoValue class). This allows the
  // entire section, including the section header, to be hidden if configured to do so.
  private updateHiddenFieldState() {
    const sectionBodyElement = this.sectionBodyRef.current
    if (!sectionBodyElement) {
      return
    }

    const formGroups = sectionBodyElement.querySelectorAll(':scope > .c-formGroup')
    if (formGroups.length === 0) {
      if (this.state.allFieldsEmpty !== false) {
        this.setState({ allFieldsEmpty: false })
      }
      return
    }

    const allHidden = Array.from(formGroups).every(group => {
      return group.classList.contains('c-formGroup--hasNoValue')
    })

    if (this.state.allFieldsEmpty !== allHidden) {
      this.setState({ allFieldsEmpty: allHidden })
    }
  }
}
